import dayjs from 'dayjs';
import type { DateValue } from '@mantine/dates';
import type { CalendarEvent } from 'src/meetings/models';

/** A meeting is "past" once its (actual or scheduled) end time is before the current minute. */
export const isPastEvent = (event: CalendarEvent): boolean => {
  const cutoff = new Date();
  cutoff.setSeconds(0, 0);
  return new Date(event.endAt ?? event.scheduledEndAt) < cutoff;
};

/** Human duration between two ISO timestamps, e.g. "30 min.", "1 hr.", "1 hr. 30 min.". */
export const formatMeetingDuration = (startIso: string, endIso: string): string => {
  const minutes = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
  if (minutes < 60) {
    return `${minutes} min.`;
  }
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours} hr.` : `${hours} hr. ${remainder} min.`;
};

export const formatEventTime = (scheduledStartAt: string, scheduledEndAt: string): string => {
  const start = new Date(scheduledStartAt);
  const end = new Date(scheduledEndAt);

  const formatTime = (date: Date): string => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${ampm}`;
  };

  return `${formatTime(start)} - ${formatTime(end)}`;
};

export const getDateSectionHeader = (date: Date): string => {
  if (isToday(date)) {
    return 'Today';
  }

  if (isTomorrow(date)) {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `Tomorrow, ${month} ${day}`;
  }

  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  return `${dayOfWeek}, ${month} ${day}`;
};

export type Order = 'asc' | 'desc';

export const groupEventsByDate = (
  events: CalendarEvent[],
  order: Order = 'asc',
): Map<string, CalendarEvent[]> => {
  const grouped = new Map<string, CalendarEvent[]>();

  for (const event of events) {
    const eventDate = new Date(event.scheduledStartAt);
    const dateKey = eventDate.toDateString();

    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }

    grouped.get(dateKey)!.push(event);
  }

  const sortedMap = new Map(
    [...grouped.entries()].sort((a, b) => {
      if (order === 'desc') {
        return new Date(b[0]).getTime() - new Date(a[0]).getTime();
      }
      return new Date(a[0]).getTime() - new Date(b[0]).getTime();
    }),
  );

  return sortedMap;
};

export const formatDuration = (startAt: string, endAt: string): string => {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const durationMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));

  if (durationMinutes < 60) {
    return `(${durationMinutes} min)`;
  }

  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;

  if (minutes === 0) {
    return `(${hours}h)`;
  }

  return `(${hours}h ${minutes}m)`;
};

const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const isTomorrow = (date: Date): boolean => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return (
    date.getDate() === tomorrow.getDate() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getFullYear() === tomorrow.getFullYear()
  );
};

export const formatEventBannerDate = (scheduledStartAt: string, scheduledEndAt: string): string => {
  const start = new Date(scheduledStartAt);
  const end = new Date(scheduledEndAt);

  const dayOfWeek = start.toLocaleDateString('en-US', { weekday: 'long' });
  const month = start.toLocaleDateString('en-US', { month: 'long' });
  const day = start.getDate();

  const formatTime = (date: Date): string => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : '';
    return `${displayHours}${displayMinutes}${ampm}`;
  };

  const duration = formatDuration(scheduledStartAt, scheduledEndAt);

  return `${dayOfWeek}, ${month} ${day} ⋅ ${formatTime(start)} - ${formatTime(end)} ${duration}`;
};

export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  return dayjs(date).format('DD MMM YYYY HH:mm');
}

export function formatDateTimeWithComma(date: string | Date | null | undefined): string {
  if (!date) return '';
  return dayjs(date).format('DD MMM, YYYY HH:mm');
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '';
  return dayjs(date).format('DD MMM YYYY');
}

export function dateMatchesSearch(date: string | Date | null | undefined, search: string): boolean {
  if (!date || !search) return false;
  return formatDateTime(date).toLowerCase().includes(search.toLowerCase());
}

// A meeting-action due date is date-only. DateInput hands back a string or a Date; dayjs parses a
// date-only string as local midnight, so formatting never shifts the day for viewers west of GMT.
export const toDueDateIso = (value: DateValue): string | null =>
  value ? dayjs(value).format('YYYY-MM-DD') : null;

export const formatDueDate = (isoDate?: string): string =>
  isoDate ? dayjs(isoDate).format('D MMM YYYY') : 'No date';
